import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getNewArrivals } from "../api";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";

const Container = styled.div`
  padding: 40px 40px 100px 40px;
  width: 100%;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  background: ${({ theme }) => theme.bg};
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding: 24px 16px 80px 16px;
  }
`;

const PageTitle = styled.h1`
  font-size: 52px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text_primary};
  margin: 0 0 8px 0;
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const BentoGrid = styled.div`
  width: 100%;
  max-width: 1200px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BentoItem = styled.div`
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: #f5f5f5;
  height: 260px;
  ${({ large }) =>
    large &&
    `
    grid-column: span 2;
    grid-row: span 2;
    height: 528px;
  `}
  &:hover img {
    transform: scale(1.04);
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
  display: block;
`;

const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProductName = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: #222;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%;
`;

const ProductPrice = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #222;
  white-space: nowrap;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 300px;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 16px;
`;

const NewArrival = () => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();

  const fetchNewArrivals = async () => {
    setLoading(true);
    try {
      const res = await getNewArrivals();
      setProducts(res.data);
    } catch (err) {
      console.error("Failed to fetch new arrivals:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewArrivals();
  }, []);

  return (
    <Container>
      <PageTitle>New Arrivals</PageTitle>

      {loading ? (
        <LoadingWrapper>
          <CircularProgress />
        </LoadingWrapper>
      ) : products.length === 0 ? (
        <EmptyState>
          <span style={{ fontSize: "40px" }}>🛍️</span>
          <span>No new arrivals yet. Check back soon!</span>
        </EmptyState>
      ) : (
        <BentoGrid>
          {products.map((product, index) => (
            <BentoItem
              key={product._id}
              large={index === 1}
              onClick={() => navigate(`/shop/${product._id}`)}
            >
              <ProductImage src={product.img} alt={product.title} />
              <Overlay>
                <ProductName>{product.title}</ProductName>
                <ProductPrice>${product.price?.org}</ProductPrice>
              </Overlay>
            </BentoItem>
          ))}
        </BentoGrid>
      )}
    </Container>
  );
};

export default NewArrival;